import { EPlanningColumns } from '../planning.type'
import {
  ArrowLineDownIcon,
  ArrowLineTopIcon,
  CurecallButton,
  CurecallCheckbox,
  CurecallInputLabel,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { PlanningTabFormValues } from './../planning.type'
import { ChangeEvent, useEffect, useId, useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'

type TColumnOption = {
  value: EPlanningColumns
  label: string
  required?: boolean
}

export function PlanningTabColumnsField() {
  const { t } = useTranslation()
  const id = useId()
  const { watch, setValue } = useFormContext<PlanningTabFormValues>()
  const { isAtLeastOneFeatureActivated } = useCurrentProject()

  const DEFAULT_COLUMNS_OPTIONS: TColumnOption[] = [
    { value: EPlanningColumns.ID, label: t('pages.planning.columns.id'), required: true },
    { value: EPlanningColumns.PATIENT, label: t('pages.planning.columns.patient') },
    { value: EPlanningColumns.MED_EVENT, label: t('pages.planning.columns.med-event') },
    {
      value: EPlanningColumns.MEDICAL_STAFF_MEMBER,
      label: t('pages.planning.columns.medical-staff-member'),
    },
    { value: EPlanningColumns.CONVOCATION, label: t('pages.planning.columns.convocation') },
    ...(isAtLeastOneFeatureActivated(['patient-documents'])
      ? [{ value: EPlanningColumns.DOCUMENTS, label: t('pages.planning.columns.documents') }]
      : []),
    {
      value: EPlanningColumns.MEDICAL_TEAM_COMMENT,
      label: t('pages.planning.columns.medical-team-comment'),
    },
    { value: EPlanningColumns.PATIENT_MED_ALERT, label: t('pages.planning.columns.patient-med-alert') },
    { value: EPlanningColumns.ACTIONS, label: t('pages.planning.columns.actions'), required: true },
  ]
  const [columnsOptions, setColumnsOptions] = useState<TColumnOption[]>(DEFAULT_COLUMNS_OPTIONS)

  const selectedColumns = watch('columns')

  useEffect(() => {
    if (selectedColumns && selectedColumns.length > 0) {
      const defaultSelectedColumns = selectedColumns
        .map((colValue) => DEFAULT_COLUMNS_OPTIONS.find((col) => col.value === colValue))
        .filter((column): column is TColumnOption => !!column)
      const remainingColumns = DEFAULT_COLUMNS_OPTIONS.filter((col) => !selectedColumns.includes(col.value))

      setColumnsOptions(defaultSelectedColumns.concat(remainingColumns))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onMoveColumn = (column: TColumnOption, newPosition: number) => {
    if (newPosition < 0 || newPosition >= columnsOptions.length) return
    const newCols = [...columnsOptions]
    const currentIndex = newCols.findIndex((col) => col.value === column.value)
    if (currentIndex !== -1) {
      newCols.splice(currentIndex, 1) // Remove the column from its current position
      newCols.splice(newPosition, 0, column) // Insert it at the new position
      setColumnsOptions(newCols)
      setValue(
        'columns',
        newCols.filter((col) => selectedColumns.includes(col.value)).map((col) => col.value),
      )
    }
  }

  const onSelectColumn = (event: ChangeEvent<HTMLInputElement>, columnToUpdate: TColumnOption) => {
    const shouldAddNewColumn = event.target.checked
    let newCols: EPlanningColumns[] = [...selectedColumns]
    if (shouldAddNewColumn) {
      // we should keep columnOption order, so we loop through columnsOptions
      const checkedColumnOptions: TColumnOption[] = [...columnsOptions].filter(
        (column) => selectedColumns.includes(column.value) || column.value === columnToUpdate.value,
      )
      newCols = checkedColumnOptions.map((column) => column.value)
    } else {
      newCols = newCols.filter((column) => column !== columnToUpdate.value)
    }
    setValue('columns', newCols)
  }

  return (
    <div>
      <CurecallInputLabel
        htmlFor={id}
        label={t('pages.planning.form.columns-title', {
          selected: selectedColumns.length,
          total: columnsOptions.length,
        })}
        isRequired={true}
        tooltipContent={t('pages.planning.form.columns-tooltip')}
        tooltipPosition="bottom"
      />
      <fieldset className="c-flex c-flex-col c-gap-1" id={id}>
        {columnsOptions.map((column, position) => {
          return (
            <div key={column.value} className="c-flex c-items-center c-gap-1">
              <CurecallCheckbox
                checked={selectedColumns.includes(column.value)}
                onChange={(event) => {
                  onSelectColumn(event, column)
                }}
                disabled={column.required}
                isDisabled={column.required}
                label={column.label + (column.required ? ` (${t('pages.planning.form.required')})` : '')}
              />
              <CurecallButton
                ariaLabelText={t('pages.planning.form.move-up')}
                appearance={ECurecallButtonAppearance.CANCEL}
                iconType={<ArrowLineTopIcon />}
                isGhostButton={true}
                onClick={() => {
                  onMoveColumn(column, position - 1)
                }}
                isDisabled={position === 0}
              />

              <CurecallButton
                ariaLabelText={t('pages.planning.form.move-down')}
                appearance={ECurecallButtonAppearance.CANCEL}
                iconType={<ArrowLineDownIcon />}
                isGhostButton={true}
                onClick={() => {
                  onMoveColumn(column, position + 1)
                }}
                isDisabled={position === columnsOptions.length - 1}
              />
            </div>
          )
        })}
      </fieldset>
    </div>
  )
}
